import * as Styles from './EventOptionsModal.styles'
import BaseModal from '../BaseModal/BaseModal'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import ShareIcon from '@mui/icons-material/Share'
import { Box } from '@mui/material'
import ShareEventModal from '../ShareEventModal/ShareEventModal'
import { useEffect, useState } from 'react'
import { Success } from '../../assets'
import {
  adminMatchmakerTrigger,
  adminStartMatchingTrigger,
} from '../../services/appsync'

type EventOptionsModalsProps = {
  open: boolean
  onClose: () => void
  eventId: string
  pushedPollId?: string | null
  onPollClick?: () => void
  onPushPoll?: (pollId: string) => void
  userEmail: string
}

const EventOptionsModals = ({
  open,
  onClose,
  eventId,
  pushedPollId,
  onPushPoll,
  onPollClick,
  userEmail,
}: EventOptionsModalsProps) => {
  const [showQrCodeDetailsModal, setShowQrCodeDetailsModal] = useState(false)
  const [selectedOption, setSelectedOption] = useState('')
  const [enableMatchmaker, setEnableMatchmaker] = useState(false)

  const generateEventLink = () => {
    const url = new URL(window.location.href)
    const baseLink = `${url.protocol}//${url.host}`
    return `${baseLink}/events?eventId=${eventId}`
  }

  const startMatchmaker = async () => {
    await adminMatchmakerTrigger(eventId, userEmail)
  }

  const handleOptionClick = (
    optionName: string,
    onClickCallback?: () => void
  ) => {
    setSelectedOption(optionName)

    if (optionName === 'matchmaker' && eventId) {
      startMatchmaker()
    }

    setTimeout(() => {
      if (onClickCallback) onClickCallback()
      setSelectedOption('')
    }, 200)
  }

  const handlePollIconClick = () => {
    if (onPollClick) {
      onClose()
      setTimeout(() => {
        onPollClick()
      }, 100) // allow modal close animation to finish
    }
  }

  const handleStartMatchmakerPairing = async () => {
    try {
      await adminStartMatchingTrigger(eventId, 'admin@admin.com')
      onClose()
    } catch (error) {
      console.error('Error starting matchmaker pairing process: ', error)
    }
  }

  useEffect(() => {
    const startMatchmakerMatching = async () => {
      await adminStartMatchingTrigger(eventId, userEmail)
    }
    if (enableMatchmaker) {
      const timer = setTimeout(() => {
        startMatchmakerMatching()
      }, 300000) // 5 min

      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMatchmaker, open])

  return (
    <>
      <BaseModal
        bottomScreenMobile={true}
        desktopWidth="411px"
        open={open}
        onClose={onClose}
      >
        <>
          {enableMatchmaker ? (
            <Box
              display="flex"
              textAlign="center"
              flexDirection="column"
              alignItems="center"
            >
              <img
                style={{ marginBottom: '24px' }}
                height="60px"
                width="60px"
                src={Success}
                alt="Success"
              />
              <Styles.ModalTitle>Success!</Styles.ModalTitle>
              <Styles.SuccessModalSubtitle>
               Checked-in attendees are now uploading a photo. Select “Start Pairing Process”
               when your attendees are ready to be matched.
              </Styles.SuccessModalSubtitle>
              <Styles.ModalButton 
                onClick={handleStartMatchmakerPairing}
                sx={{mt: 3 }}
              >
                Start Pairing Process
              </Styles.ModalButton>
            </Box>
          ) : (
            <>
              <Styles.ModalTitle>Event Options</Styles.ModalTitle>
              <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
              >
                <Styles.ModalOptions
                  disableRipple
                  variant="text"
                  startIcon={<AssessmentOutlinedIcon />}
                  onClick={() => {
                    handleOptionClick('polls')
                    handlePollIconClick()
                  }}
                  sx={{
                    border:
                      selectedOption === 'polls'
                        ? ' 1px solid #002FAF'
                        : 'none',
                    borderRadius: '24px',
                  }}
                >
                  Polls
                </Styles.ModalOptions>
                <Styles.ModalOptions
                  disableRipple
                  variant="text"
                  startIcon={<PeopleOutlineIcon />}
                  onClick={() =>
                    handleOptionClick('matchmaker', () =>
                      setEnableMatchmaker(true)
                    )
                  }
                  sx={{
                    border:
                      selectedOption === 'matchmaker'
                        ? ' 1px solid #002FAF'
                        : 'none',
                    borderRadius: '24px',
                  }}
                >
                  Matchmaker
                </Styles.ModalOptions>
                <Styles.ModalOptions
                  disableRipple
                  variant="text"
                  startIcon={<ShareIcon />}
                  onClick={() => {
                    setShowQrCodeDetailsModal(true)
                  }}
                  sx={{
                    border:
                      selectedOption === 'share'
                        ? ' 1px solid #002FAF'
                        : 'none',
                    borderRadius: '24px',
                  }}
                >
                  Share Event
                </Styles.ModalOptions>
              </Box>

              <ShareEventModal
                link={generateEventLink()}
                open={showQrCodeDetailsModal}
                onClose={() => {
                  setShowQrCodeDetailsModal(false)
                }}
              />
            </>
          )}
        </>
      </BaseModal>
    </>
  )
}
export default EventOptionsModals
